import HighlightedText from "~/components/HighlightedText";
import styles from "./styles.module.scss";
import { Badge } from "~/components/ui/badge";
import { Checkbox } from "~/components/ui/checkbox";
import { Spinner } from "~/components/ui/spinner";
import { Input } from "~/components/ui/input";
import { ChevronDown, Search as SearchIcon, X } from "lucide-react";
import cn from "classnames";
import { useEffect, useCallback, useState, useMemo, Dispatch, SetStateAction } from "react";
import debounce from "lodash/debounce";
import { usePopupFocusManager } from "~/util/hooks";
import _ from "lodash";

export type TreeOption<K> = {
  id: K;
  tid?: K;
  label: string;
  children?: TreeOption<K>[];
  parent?: K;
};

type SelectedOptions<K> = Set<K>;

export interface Props<K, O extends TreeOption<K>> {
  loading: boolean;
  options: O[];
  selected: SelectedOptions<K>;
  onChange: (value: SelectedOptions<K>) => void;
  // Load items (needed for lazy-load)
  load?: () => Promise<any>;
  infoBoxText: string;
  maxOptionsToSelect?: number;
  // items by id
  byId: Map<K, O>;
  highlevelSelect?: boolean;
  defaultCollapsed?: Set<K>;
  invalid?: boolean;
}

function subtreeIds<K, O extends TreeOption<K>>(subtree: O, maxOptionsToSelect: number | null = null) {
  let ids: SelectedOptions<K> = new Set([]);
  const considerTree = (t: TreeOption<K>) => {
    ids.add(t.id);
    if (
      !t.children ||
      t.children.length === 0 ||
      (maxOptionsToSelect !== null && ids.size + t.children.length >= maxOptionsToSelect)
    ) {
      return;
    }
    for (const child of t.children) {
      considerTree(child);
    }
  };
  considerTree(subtree);
  return ids;
}

// Render a subtree of options
function renderOptionArray<K, T extends TreeOption<K>>(
  options: T[],
  level = 0,
  selectedOptions: SelectedOptions<K>,
  setselected: (topicIds: SelectedOptions<K>) => void,
  searchFor: string | null = null,
  maxOptionsToSelect: number | null = null,
  byId: Map<K, T>,
  collapsed: Set<K>,
  setCollapsed: Dispatch<SetStateAction<Set<K>>>,
  setExpanded: Dispatch<SetStateAction<boolean>>,
  clearSearch: () => void,
  highlevelSelect: boolean
) {
  const selectedMax = maxOptionsToSelect !== null && selectedOptions.size >= maxOptionsToSelect;
  const rootLevel = level === 0;
  const result = options
    .map((option: TreeOption<K>) => {
      let id_value = option.id;
      if (option.tid) id_value = option.tid;
      const selected = selectedOptions.has(+id_value as K) || selectedOptions.has(id_value.toString() as K);
      const searchIn = option.label ? option.label.toLowerCase() : "";
      const searchForLower = searchFor !== null ? searchFor.toLowerCase() : null;
      const searchStartIdx = searchForLower !== null ? searchIn.indexOf(searchForLower) : null;
      const searchRange =
        searchFor !== null && searchStartIdx !== null && searchStartIdx !== -1
          ? ([searchStartIdx, searchStartIdx + searchFor.length] as [number, number])
          : null;
      let thisLabelMatchesSearch = searchStartIdx !== -1;
      if (rootLevel && !highlevelSelect) {
        thisLabelMatchesSearch = false;
      }
      const someChildrenMatchSearch = () => {
        let result = false;
        function iter(a) {
          if (!!a.label && a.label.toLowerCase().indexOf(searchForLower) > -1) {
            result = true;
            return;
          }
          return Array.isArray(a.children) && a.children.some(iter);
        }

        option.children?.some(iter);
        return result;
      };
      const includesFilteredEntry = thisLabelMatchesSearch || someChildrenMatchSearch();

      const isCollapsed = collapsed.has(id_value);
      let renderedSubtree = null;

      if ((isCollapsed || searchFor !== null) && option.children !== undefined && option.children.length !== 0) {
        const renderedTree = renderOptionArray(
          option.children,
          level + 1,
          selectedOptions,
          setselected,
          searchFor,
          maxOptionsToSelect,
          byId,
          collapsed,
          setCollapsed,
          setExpanded,
          clearSearch,
          highlevelSelect
        );
        if (renderedTree !== null) {
          renderedSubtree = <div className={`${styles.topicSubtree}`}>{renderedTree}</div>;
        }
      }

      const shouldDisplay = thisLabelMatchesSearch || renderedSubtree !== null;

      // if (!shouldDisplay || !option.label) return null;

      const renderedTextLabel = (
        <span>{searchRange !== null ? <HighlightedText text={option.label} range={searchRange} /> : option.label}</span>
      );

      const disabled = !selected && selectedMax;
      const showCheckBox = () => {
        return highlevelSelect ? true : _.isEmpty(option.children);
      };

      const onRootClick = (evt) => {
        setCollapsed((prevState: Set<K>) => {
          let newState: Set<K> = new Set<K>([...prevState]);
          if (newState.has(id_value)) {
            newState.delete(id_value);
          } else {
            newState.add(id_value);
          }
          return newState;
        });
      };

      if (!!searchFor && !includesFilteredEntry) {
        return null;
      }

      return (
        <div
          key={id_value as any as string}
          className={cn({
            [styles.topicSubtreeRoot]: true,
            [styles.rootTree]: rootLevel,
          })}
          style={{ marginLeft: level * 16 }}
        >
          <div className={styles.topicSubtreeRootCheckbox}>
            <div
              className={styles.topicSubtreeRootArrow}
              style={{ width: level * 16, marginLeft: -(level * 16) - 1 }}
            />
            <div className={styles.treeSelectCheckBoxWrapper}>
              {showCheckBox() ? (
                <Checkbox
                  id={`topic-select-${id_value}`}
                  checked={selected}
                  disabled={disabled}
                  title={disabled ? `Cannot select more than ${maxOptionsToSelect} options` : undefined}
                  onCheckedChange={(checked) => {
                    let node = option;
                    let id_value = node.id;
                    if (node.tid) id_value = node.tid;
                    if (checked) {
                      selectedOptions.add(id_value);
                      setExpanded(false);
                      clearSearch();
                    } else {
                      selectedOptions.delete(id_value);
                    }
                    setselected(selectedOptions);
                  }}
                />
              ) : null}
              <div onClick={onRootClick}>{renderedTextLabel}</div>
            </div>
            {!!option.children?.length && (
              <ChevronDown
                size={16}
                onClick={onRootClick}
                color="#77a0b6"
                className={styles.rootTreeAngleIcon}
              />
            )}
          </div>
          {renderedSubtree}
        </div>
      );
    })
    .filter((a) => a !== null);
  // if (result.length === 0) return null;
  return result;
}

function TreeSelect<K, O extends TreeOption<K>>(props: Props<K, O>) {
  const { loading, options, selected, onChange, byId, highlevelSelect, defaultCollapsed, invalid } = props;
  // textSearchValue drives (debounced) filtering; searchInput is the immediate
  // input value so typing stays responsive (was masked by the old WFP-UI Search).
  const [textSearchValue, setTextSearchValue] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  const [collapsed, setCollapsed] = useState(new Set<K>());
  useEffect(() => {
    if (defaultCollapsed) {
      setCollapsed(defaultCollapsed);
    }
  }, [defaultCollapsed]);

  const focusManager = usePopupFocusManager();
  const expanded = focusManager.focused;
  const setExpanded = focusManager.setFocused;

  const debouncedSetSearch = useMemo(
    () =>
      debounce((v: string) => {
        setTextSearchValue(v);
      }, 500),
    []
  );
  const onSearchChange = useCallback(
    (v: string) => {
      setSearchInput(v);
      debouncedSetSearch(v);
    },
    [debouncedSetSearch]
  );
  const clearSearch = useCallback(() => {
    setSearchInput("");
    setTextSearchValue("");
  }, []);

  const renderedTree = useMemo(() => {
    if (loading) {
      return (
        <div className="flex h-full w-full py-4 items-center justify-center">
          <Spinner className="size-5" />
        </div>
      );
    }
    const renderedRootLevel = renderOptionArray(
      options,
      0,
      selected,
      onChange,
      textSearchValue.trim() === "" ? null : textSearchValue,
      props.maxOptionsToSelect,
      byId,
      collapsed,
      setCollapsed,
      setExpanded,
      clearSearch,
      highlevelSelect
    );
    if (renderedRootLevel === null || renderedRootLevel.length === 0) {
      return <div className="text-sm w-full h-full flex items-center justify-center">No matches</div>;
    }
    return <div>{renderedRootLevel}</div>;
  }, [options, selected.size, textSearchValue, loading, props.maxOptionsToSelect, collapsed]);

  const [loadedInitial, setLoadedInitial] = useState(false);
  // Load when first focused
  useEffect(() => {
    if (!loadedInitial && expanded && !!props.load) {
      props.load();
      setLoadedInitial(true);
    }
  }, [expanded]);

  // Expand automatically if user starts typing
  useEffect(() => {
    if (textSearchValue === "") return;
    setExpanded(true);
  }, [textSearchValue]);

  const selectedOptionsDisplay = useMemo(() => {
    return Array.from(selected).map((id) => {
      const option = props.byId.get(id);
      if (option === undefined) return null;
      const optionDisplay = option.label;
      return (
        <Badge key={id as any} variant="default" className="mr-1.5 mb-1.5 gap-1 normal-case" asChild>
          <button
            type="button"
            aria-label={`Remove ${optionDisplay}`}
            className="cursor-pointer hover:bg-info-300"
            onClick={() => {
              const selectedCopy = new Set(selected);
              selectedCopy.delete(option.tid || option.id);
              onChange(selectedCopy);
            }}
          >
            <span className="text-xs">{optionDisplay}</span>
            <X />
          </button>
        </Badge>
      );
    });
  }, [selected.size, props.byId, selected, onChange]);
  return (
    <div ref={focusManager.ref} className={invalid ? "tree-select--invalid" : ""}>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="topics-search"
          name="search-query"
          autoComplete="off"
          placeholder="Search..."
          value={searchInput}
          className="pl-9 pr-9"
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setExpanded(true)}
        />
        {!!searchInput && (
          <button
            type="button"
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={clearSearch}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      <div className={styles.wrapper}>
        <div className="mt-2" style={{ zIndex: 0 }}>
          {selectedOptionsDisplay}
        </div>
        <div className={styles.options}>
          {expanded && (
            <div
              className={styles.documentViewWrapper}
              style={{ boxShadow: "0px 0px 2px 0px #00000017, 0px 0px 8px 0px #00000012" }}
            >
              <div className={styles.resultsContainer}>{renderedTree}</div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 border-l-4 border-primary-600 bg-primary-50 p-3">
        <strong className="mb-1 block text-sm">TIPS &amp; INFO</strong>
        <div className="text-sm">{props.infoBoxText}</div>
      </div>
    </div>
  );
}

export default TreeSelect;
